import type { TimelineEventType } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event";
import { useHandler } from "@redotech/react-util/hook";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { useMemo, useState } from "react";
import {
  allGroupIds,
  getGroupIdForEventType,
  isGroupId,
  timelineEventGroups,
} from "./timeline-event-filter-options";

const TIMELINE_EVENT_FILTERS_KEY =
  "redo.support-v2.conversation-timeline.group-ids";

function normalizeSelectedGroupIds(value: readonly string[]): string[] {
  const unique = [...new Set(value)];
  if (unique.length === 0) {
    return [...allGroupIds];
  }
  return unique;
}

function readSelectedGroupIdsFromSessionStorage(): string[] {
  if (typeof window === "undefined") {
    return [...allGroupIds];
  }

  const rawValue = window.sessionStorage.getItem(TIMELINE_EVENT_FILTERS_KEY);
  if (!rawValue) {
    return [...allGroupIds];
  }

  try {
    const parsed: unknown = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return [...allGroupIds];
    }

    const selectedIds = parsed.filter(
      (entry): entry is string => typeof entry === "string" && isGroupId(entry),
    );

    return normalizeSelectedGroupIds(selectedIds);
  } catch {
    return [...allGroupIds];
  }
}

function writeSelectedGroupIdsToSessionStorage(value: readonly string[]): void {
  if (typeof window === "undefined") {
    return;
  }
  window.sessionStorage.setItem(
    TIMELINE_EVENT_FILTERS_KEY,
    JSON.stringify(value),
  );
}

export interface UseTimelineEventFiltersResult {
  filterItems: RedoListItem<string>[];
  selectedFilterItems: RedoListItem<string>[];
  hasActiveFilters: boolean;
  isEventVisible(eventType: TimelineEventType): boolean;
  clearFilters(): void;
  setSelectedFilterItems(items: RedoListItem<string>[]): void;
}

export function useTimelineEventFilters(): UseTimelineEventFiltersResult {
  const [selectedGroupIds, setSelectedGroupIds] = useState<readonly string[]>(
    readSelectedGroupIdsFromSessionStorage,
  );

  const filterItems = useMemo<RedoListItem<string>[]>(
    () => [
      { id: "__header__", type: "section", text: "Include:", value: "" },
      ...timelineEventGroups.map((group) => ({
        id: group.id,
        type: "text" as const,
        text: group.label,
        value: group.id,
      })),
    ],
    [],
  );

  const selectedGroupIdSet = useMemo(
    () => new Set(selectedGroupIds),
    [selectedGroupIds],
  );

  const selectedFilterItems = useMemo(
    () => filterItems.filter((item) => selectedGroupIdSet.has(item.value)),
    [filterItems, selectedGroupIdSet],
  );

  const setSelectedGroupIdsAndPersist = useHandler(
    (nextValue: readonly string[]) => {
      const normalized = normalizeSelectedGroupIds(nextValue);
      writeSelectedGroupIdsToSessionStorage(normalized);
      setSelectedGroupIds(normalized);
    },
  );

  const setSelectedFilterItems = useHandler((items: RedoListItem<string>[]) => {
    const nextIds = items.map((item) => item.value);
    setSelectedGroupIdsAndPersist(nextIds);
  });

  const clearFilters = useHandler(() => {
    setSelectedGroupIdsAndPersist(allGroupIds);
  });

  const isEventVisible = useHandler((eventType: TimelineEventType) => {
    const groupId = getGroupIdForEventType(eventType);
    return groupId !== undefined && selectedGroupIdSet.has(groupId);
  });

  return {
    filterItems,
    selectedFilterItems,
    hasActiveFilters: selectedGroupIds.length !== allGroupIds.length,
    isEventVisible,
    clearFilters,
    setSelectedFilterItems,
  };
}
